import { humanise } from './format';
import { TREND_FORMS } from './localOperations';
import { type Block, type Envelope, type LensDocument, type LensDocumentData, type Param, type PlannedBlock, blockKind } from './types';

const GRAIN_WORDS: Record<string, string> = { day: 'daily', week: 'weekly', month: 'monthly', quarter: 'quarterly', year: 'yearly' };

export function boardLabels(document: LensDocument, data?: LensDocumentData): Map<string, string> {
  const known = new Map<string, string>();
  for (const envelope of Object.values(data?.queries ?? {})) for (const column of envelope.columns) known.set(column.name, column.label);
  for (const [id, measure] of Object.entries(document.measures)) if (measure.label) known.set(id, measure.label);
  return known;
}

export function paramOptionLabel(param: Param | undefined, labels: Map<string, string>, option: string): string {
  const role = param && 'sql' in param ? param.role : undefined;
  if (role === 'grain') return GRAIN_WORDS[option.toLowerCase()] ?? option;
  if (role === 'dimension') return /^(all|none)$/i.test(option) ? 'overall' : `by ${option.toLowerCase()}`;
  if (role === 'measure') return option;
  if (option === 'none') return 'overall';
  if (param && 'columns' in param) return `by ${(labels.get(option) ?? humanise(option)).toLowerCase()}`;
  return labels.get(option) ?? humanise(option);
}

export type BlockTitlePart = string | { param: string };

function stringTitleParts(text: string): Array<BlockTitlePart> {
  return text
    .replace(/\s*·\s*(?=\{\$)/g, ' ')
    .split(/(\{\$[a-z0-9_]+\})/i)
    .map((part) => (/^\{\$/.test(part) ? { param: part.slice(2, -1) } : part));
}

function tokenPart(value: unknown, labels: Map<string, string>): BlockTitlePart {
  const text = String(value ?? '');
  if (text.startsWith('$')) return { param: text.slice(1) };
  return labels.get(text) ?? humanise(text);
}

function joinBlockTitle(parts: Array<BlockTitlePart>, document: LensDocument, data?: LensDocumentData): string {
  const labels = boardLabels(document, data);
  return parts
    .map((part) =>
      typeof part === 'string'
        ? part
        : paramOptionLabel(document.params[part.param], labels, String(data?.params[part.param] ?? '')),
    )
    .join('');
}

export function plainTitle(text: string, document: LensDocument, data?: LensDocumentData): string {
  return joinBlockTitle(stringTitleParts(text), document, data);
}

export function shownKind(block: Block, planned?: PlannedBlock) {
  const own = blockKind(block);
  return planned?.kind && own && TREND_FORMS.includes(planned.kind) && TREND_FORMS.includes(own)
    ? own
    : (planned?.kind ?? own);
}

export function blockTitleParts(
  block: Block,
  document: LensDocument,
  data?: LensDocumentData,
  planned?: PlannedBlock,
  envelope?: Envelope,
): Array<BlockTitlePart> | undefined {
  if (typeof block.title === 'string' && block.title) return stringTitleParts(block.title);
  const labels = boardLabels(document, data);
  const kind = shownKind(block, planned);
  const by = block.by?.[0];
  const byLabel =
    by && !by.startsWith('$')
      ? (envelope?.columns.find((column) => column.name === by)?.label ?? humanise(by))
      : undefined;
  const split: Array<BlockTitlePart> = by?.startsWith('$')
    ? [' ', tokenPart(by, labels)]
    : byLabel
      ? [` by ${byLabel.toLowerCase()}`]
      : [];
  if (planned?.family === 'table') return ['Detail'];
  if (planned?.family === 'bridge' && kind) return [tokenPart(block[kind], labels), ' change'];
  if ((planned?.family === 'time' || planned?.family === 'breakdown') && kind)
    return [tokenPart(block[kind], labels), ...split];
  return undefined;
}

export function renderedBlockTitle(
  block: Block,
  document: LensDocument,
  data?: LensDocumentData,
  planned?: PlannedBlock,
  envelope?: Envelope,
) {
  const parts = blockTitleParts(block, document, data, planned, envelope);
  if (!parts) return undefined;
  return joinBlockTitle(parts, document, data);
}
